/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

import React, { useState, useEffect, useContext, useRef } from 'react';
import { FieldArray, Field, Form, Formik } from 'formik';
import * as yup from 'yup';
import Icon from 'components/Icon';
import {
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCollum,
  InlineLabel,
  ViewText,
  ButtonContainer,
  ButtonContent,
  LabelEdit
} from './styles';
import { GridColumnTitle, Label } from 'components/Form';
import Row from 'components/Grid/Row';
import Col from 'components/Grid/Col';
import InputField from 'components/Form/InputField';
import { FitinsurAPI, MessageCenter } from 'utils';
import Button from 'components/Button';
import { GlobalLoaderContext, GlobalAuthContext } from 'store/context';
import DragZoneUpload from 'components/DragZoneUpload'
import ModalConfirmDelete from 'components/ModalConfirmDelete';
import ComboBox from 'components/Form/ComboBox3';

const EmployeeInformation = ({ idPerson, columns, data, legalEntity, onSavePersonRelationship, options }) => {
  const formikRef = useRef(null);
  const inputRef = useRef(null);
  const fitinsur = new FitinsurAPI();
  const mc = new MessageCenter();
  const loading = useContext(GlobalLoaderContext);
  const authContext = useContext(GlobalAuthContext);

  const [sortBy, setSortBy] = useState({ key: null, direction: null, });
  const [addPersonRelationship, setAddPersonRelationship] = useState(false);
  const [idPersonRelationship, setIdPersonRelationship] = useState(null);
  const [validations, setValidations] = useState({});
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [initial, setInitial] = useState({
    personTailLastName: '',
    idPersonTail: '',
    personHeadJobTitle: '',
    signatureImageId: '',
    signatureImageSrc: ''
  });

  const setEditPersonRelationship = (personRelationship) => {
    formikRef.current.values.personTailLastName = personRelationship.personTailLastName;
    formikRef.current.values.idPersonTail = personRelationship.idPersonTail;
    formikRef.current.values.personHeadJobTitle = personRelationship.relationshipContent.personHeadJobTitle;
    formikRef.current.values.signatureImageId = personRelationship.signatureImageId;
    formikRef.current.values.signatureImageSrc = personRelationship.signatureImageSrc;

    setAddPersonRelationship(true);
    setIdPersonRelationship(personRelationship.idPersonRelationship);
    setTimeout(() => inputRef.current && inputRef.current.focus(), 100)
  }

  const resetForm = () => {
    formikRef.current.values.personTailLastName = '';
    formikRef.current.values.idPersonTail = '';
    formikRef.current.values.personHeadJobTitle = '';
    formikRef.current.values.signatureImageId = '';
    formikRef.current.values.signatureImageSrc = '';
  }

  const saveRelationship = async (body) => {
    try {
      loading.start();
      body.idPersonHead = idPerson;
      body.idPersonTail = legalEntity;
      body.idPersonRelationship = idPersonRelationship;
      body.idDomain = 291;
      body.relationshipContent = {
        personHeadJobTitle: body.personHeadJobTitle,
        personContactDepartment: {
          idAnswerOption: body.contactDepartment.idAnswerOption,
          description: body.contactDepartment.description,
        },
      }

      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur.requestSavePersonRelationship(body).then(res => res.json());
      if (response.ret_code !== 200) {
        mc.error(response.ret_msg_details)
        loading.stop();
        return;
      }

      setIdPersonRelationship(null);
      setAddPersonRelationship(false);
      resetForm();
      onSavePersonRelationship && onSavePersonRelationship();
    } catch (err) {
      loading.stop();
      mc.error("An error happened, please try again.");
    };
  }

  const deletePersonRelationship = async (idPersonRelationship) => {
    try {
      loading.start();
      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur.requestDeletePersonRelationship(idPersonRelationship).then(res => res.json());
      setIdPersonRelationship(null);

      if (response.ret_code !== 200) {
        mc.error(response.ret_msg_details)
        loading.stop();
        return;
      }

      onSavePersonRelationship && onSavePersonRelationship();
    } catch (err) {
      setIdPersonRelationship(null);
      loading.stop();
      mc.error("An error happened, please try again.");
    };
  }

  const createValidation = () => {
    let validations = yup.object().shape({
      contactDepartment: yup.string().required("* Required field"),
    });
    setValidations(validations);
    return validations
  }

  useEffect(() => {
    createValidation();
  }, []);

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initial}
      enableReinitialize={true}
      onSubmit={(values) => saveRelationship(values)}
      validationSchema={validations}
    >
      {({ setFieldValue, values }) => (
        <>
          <ModalConfirmDelete show={deleteModalOpen} onModalClose={() => { setDeleteModalOpen(false); setIdPersonRelationship(null); }} onConfirm={() => deletePersonRelationship(idPersonRelationship)} />
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((column, index) =>
                  <TableHeader right={column.right} key={index} minWidth={column.minWidth} maxWidth={column.maxWidth}>
                    <GridColumnTitle style={{ cursor: column.value ? 'pointer' : 'not-allowed' }}>
                      {column.label}
                    </GridColumnTitle>
                    {column.value && sortBy.key === column.value && sortBy.direction === "asc" ?
                      <Icon name={"arrowup"} /> :
                      column.value && sortBy.key === column.value && sortBy.direction === "desc" ?
                        <Icon name={"arrowdown"} /> :
                        ''}
                  </TableHeader>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              <FieldArray
                name="management"
                render={() => data.map((item, index) => (
                  <TableRow key={index} >
                    <TableCollum>
                      <InlineLabel>
                        Contact Department
                      </InlineLabel>
                      <LabelEdit fontSize={12}>
                        {item.relationshipContent?.personContactDepartment?.description}
                      </LabelEdit>
                    </TableCollum>

                    <TableCollum>
                      <InlineLabel>
                        Job Title
                      </InlineLabel>
                      <LabelEdit fontSize={12}>
                        {item.relationshipContent.personHeadJobTitle}
                      </LabelEdit>
                    </TableCollum>

                    <TableCollum right>
                      <InlineLabel>
                        ACTION
                      </InlineLabel>
                      <Label>
                        <Icon
                          name="edit"
                          style={{ alignSelf: "center", cursor: "pointer" }}
                          onClick={() => setEditPersonRelationship(item)}
                        />
                        <Icon
                          name="trash"
                          style={{ alignSelf: "center", cursor: "pointer" }}
                          onClick={() => {
                            setIdPersonRelationship(item.idPersonRelationship);
                            setDeleteModalOpen(true);
                            setAddPersonRelationship(false);
                            resetForm();
                          }} />
                      </Label>
                    </TableCollum>
                  </TableRow>
                ))}
              />
            </TableBody>
          </Table>

          {!addPersonRelationship && !data.length && <Row>
            <Col lg={6} md={6} sm={6} xs={12}></Col>
            <Col lg={6} md={6} sm={6} xs={12}>
              <ViewText style={{ marginTop: '15px' }}>
                <Icon name="plus" onClick={() => setAddPersonRelationship(true)} style={{ cursor: 'pointer' }} />
              </ViewText>
            </Col>
          </Row>}


          {addPersonRelationship && <Form>
            <div style={{ marginTop: 40, marginBottom: 10 }}>
              <Row>
                <Col lg={6} md={6} sm={6} xs={12}>
                  <Field
                    name="contactDepartment"
                    label="Contact Department"
                    options={options.contactDepartments.map(contact => ({
                      label: contact.description,
                      value: {
                        ...contact,
                      }
                    }))}
                    searchList={[]}
                    onChange={(e) => {
                      setFieldValue('contactDepartment', e)
                    }}
                    component={ComboBox}
                    isRequired
                    value={values.contactDepartment}
                  />
                </Col>

                <Col lg={6} md={6} sm={6} xs={12}>
                  <Field
                    name="personHeadJobTitle"
                    label="Job Title"
                    component={InputField}
                  />
                </Col>

                <Col lg={6} md={6} sm={6} xs={12}>
                  <Label>Signature</Label>
                  <DragZoneUpload
                    autoUpload={true}
                    height="30"
                    multiple={false}
                    documentTypeRequired={false}
                    onSuccess={([result]) => {
                      if (result) {
                        setFieldValue('signatureImageSrc', result.fileUrl);
                        setFieldValue('signatureImageId', result.documentId)
                      }
                    }}
                  />
                </Col>

                <Col lg={6} md={6} sm={6} xs={12}>
                  {values.signatureImageSrc && <img style={{ display: 'block', maxWidth: 250, marginTop: 10 }} src={values.signatureImageSrc} />}
                </Col>
              </Row>

              <ButtonContainer>
                <ButtonContainer style={{ justifyContent: 'flex-start', alignItems: 'center', paddingBottom: '10px' }} />
                <ButtonContent>
                  <Button
                    fullWidth
                    style={{ marginLeft: 15 }}
                    third
                    onClick={() => {
                      setIdPersonRelationship(null);
                      setAddPersonRelationship(false);
                      resetForm();
                    }}
                  >
                    CANCEL
                  </Button>
                </ButtonContent>
                <ButtonContent>
                  <Button
                    fullWidth
                    style={{ marginLeft: 15 }}
                    type="submit"
                    primary
                    onClick={() => {
                      createValidation().validate(values).catch((e) => {
                        console.log('validation error', e)
                        mc.error('Please fill in all required fields.')
                      })
                    }}
                    id="btnSubmit">
                    {idPersonRelationship ? "SAVE" : "ADD"}
                  </Button>
                </ButtonContent>
              </ButtonContainer>
            </div>
          </Form>}
        </>
      )}
    </Formik>
  )
}

export default EmployeeInformation;